import { useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { imageLookupKey, tradePhotoFor } from '../utils/notificationDisplay';

/**
 * Photos for notifications created before photos were stored on them
 * (server-side since 2026-10-12). Each such notification is resolved from
 * what it already records (see imageLookupKey): the listing it names, or the
 * trade, purchase or interest it belongs to, always picking the pedal that
 * matters to the viewer (in a trade, the one they are trading FOR).
 *
 * Runs only while the panel is open. One read per key per app session:
 * results, including "no photo", are cached at module level, so reopening the
 * panel costs nothing. Every doc read here is allowed by firestore.rules
 * (listings, tradeOffers and purchaseOffers are publicly readable;
 * listingInterest by its two parties).
 *
 * @returns {Record<string,string>}  lookup key -> original photo URL
 */
const cache = new Map(); // `${uid}|${key}` -> url | null (per viewer: a trade's photo depends on who is looking)
const MAX_LOOKUPS_PER_OPEN = 20;

const listingImage = (data) => data?.images?.top || data?.images?.front || data?.placeholderImage?.url || null;

async function readListingImage(id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, 'listings', id));
  return snap.exists() ? listingImage(snap.data()) : null;
}

const viewer = () => auth.currentUser?.uid || '';
const cacheKey = (key) => `${viewer()}|${key}`;

async function resolve(key) {
  const [kind, id] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
  if (kind === 'listing') return readListingImage(id);
  if (kind === 'trade') {
    const snap = await getDoc(doc(db, 'tradeOffers', id));
    if (!snap.exists()) return null;
    const photo = tradePhotoFor(snap.data(), auth.currentUser?.uid);
    return photo.image || readListingImage(photo.listingId);
  }
  if (kind === 'purchase') {
    // One pedal in a purchase: the listing being bought, for buyer and seller.
    const snap = await getDoc(doc(db, 'purchaseOffers', id));
    if (!snap.exists()) return null;
    const data = snap.data();
    return listingImage(data.pedalSnapshot?.targetPedal) || readListingImage(data.targetPedalId);
  }
  if (kind === 'interest') {
    // The listing the interest is about: the receiver's own pedal, or the one
    // the sender expressed interest in.
    const snap = await getDoc(doc(db, 'listingInterest', id));
    if (!snap.exists()) return null;
    const data = snap.data();
    return data.listingSnapshot?.imageUrl || readListingImage(data.listingId);
  }
  return null;
}

export function keysNeedingImage(notifications, hasImage) {
  const keys = [];
  for (const n of notifications) {
    if (hasImage(n)) continue;
    const key = imageLookupKey(n);
    if (!key || cache.has(cacheKey(key)) || keys.includes(key)) continue;
    keys.push(key);
  }
  return keys.slice(0, MAX_LOOKUPS_PER_OPEN);
}

function cachedImages() {
  const prefix = `${viewer()}|`;
  const out = {};
  cache.forEach((url, k) => { if (url && k.startsWith(prefix)) out[k.slice(prefix.length)] = url; });
  return out;
}

export function useNotificationListingImages(notifications, active, hasImage) {
  const [images, setImages] = useState(cachedImages);
  // Keys that failed during this open: not retried until the panel reopens.
  // Without this, a failed read (offline) re-rendered the panel, which re-ran
  // the effect, which read again: an endless loop while the panel was open.
  const failedRef = useRef(new Set());
  // The rows arrive as a fresh array every render; key the effect on what
  // actually needs fetching instead.
  const pending = active
    ? keysNeedingImage(notifications, hasImage).filter((k) => !failedRef.current.has(k))
    : [];
  const pendingSignature = pending.join(',');

  useEffect(() => {
    if (!active) failedRef.current = new Set();
  }, [active]);

  useEffect(() => {
    if (!pendingSignature) return undefined;
    const keys = pendingSignature.split(',');
    let cancelled = false;
    Promise.all(keys.map(async (key) => {
      try {
        cache.set(cacheKey(key), (await resolve(key)) || null);
        return true;
      } catch (e) {
        // Offline, deleted, or not readable: the icon fallback stays.
        failedRef.current.add(key);
        return false;
      }
    })).then((results) => {
      if (cancelled) return;
      // Only when something new landed: a failed-only pass must not trigger
      // another render-and-fetch round.
      if (results.some(Boolean)) setImages(cachedImages());
    });
    return () => { cancelled = true; };
  }, [pendingSignature]);

  return images;
}
